import { ChatMessageUrlPrewiewWrapper } from '@src/components/LinkPreview';
import RenderMarkdown from '@src/components/RTEditor/RenderMarkdown';
import { SpecialMessageParser } from '@src/logic/specialmessageprocessing';
import { MediaType } from '@src/model/frontendmodel';
import { TimelineEvent } from '@src/types';
import { itsJustOneUrl } from '@src/utils/validation';
import { useMemo } from 'react';
import ChatMessageContact from '../ChatMessage/ChatMessageContact';
import ChatMessageAudio from './ChatMessageAudio';
import ChatMessageFile from './ChatMessageFile';
import ChatMessageImage from './ChatMessageImage';
import ChatMessageLocation from './ChatMessageLocation';
import ChatMessageProductPreviewWrapper from './ChatMessageProductPreview';
import ChatMessageSticker from './ChatMessageSticker';
import ChatMessageVideo from './ChatMessageVideo';
import ChatMessageWsCatalogueShoppingCartWrapper from './ChatMessageWsCatalogueShoppingCart';
import ChatMessageWSFlowResponse from './ChatMessageWSFlowResponse';
import ChatMessageWSFlowTemplate from './ChatMessageWSFlowTemplate';

type ChatMessageContentProps = {
  message: Partial<TimelineEvent>;
  isIncomingMsg?: boolean;
  isOutgoingMsg?: boolean;
};

const ChatMessageContent: React.FC<ChatMessageContentProps> = ({
  message,
  isIncomingMsg,
}) => {
  const messageContent = useMemo(() => {
    switch (message?.mediaType) {
      case MediaType.IMAGE:
        return (
          <ChatMessageImage
            mediaUrl={message.mediaUrl || ''}
            caption={message.isCaptionDefined ? message?.text : ''}
            mediaSize={message.mediaSize}
          />
        );

      case MediaType.STICKER:
        return <ChatMessageSticker mediaUrl={message.mediaUrl || ''} />;

      case MediaType.AUDIO:
        return (
          <ChatMessageAudio
            mediaUrl={message.mediaUrl || ''}
            color={isIncomingMsg ? 'primary' : 'secondary'}
          />
        );

      case MediaType.VIDEO:
        return (
          <ChatMessageVideo
            mediaUrl={message.mediaUrl || ''}
            caption={message.isCaptionDefined ? message?.text : ''}
            mediaSize={message.mediaSize}
          />
        );

      case MediaType.FILE:
        if (message?.mediaUrl)
          return (
            <ChatMessageFile
              mediaName={message?.text ?? ''}
              mediaUrl={message.mediaUrl}
            />
          );
        break;

      case MediaType.LOCATION:
        if (!!message?.longitude && !!message?.latitude) {
          return (
            <ChatMessageLocation
              longitude={message?.longitude}
              latitude={message?.latitude}
              locationName={message?.locationName}
              address={message?.address}
            />
          );
        }
        break;

      case MediaType.PRODUCT_OVERVIEW:
        return <ChatMessageProductPreviewWrapper message={message} />;

      case MediaType.SHOPPING_CART:
        return <ChatMessageWsCatalogueShoppingCartWrapper message={message} />;

      case MediaType.FLOW:
        return <ChatMessageWSFlowResponse message={message} />;

      case MediaType.TEMPLATE:
        return <ChatMessageWSFlowTemplate message={message} />;

      //Only applies to messages containing only one URL
      case MediaType.TEXT: {
        if (itsJustOneUrl(message?.text))
          return (
            <ChatMessageUrlPrewiewWrapper
              isIncomingMsg={isIncomingMsg}
              message={message}
            />
          );

        break;
      }
      case MediaType.CONTACTS:
        return <ChatMessageContact contacts={message.contacts} />;

      default:
    }

    if (
      message.quotedMessage &&
      message.quotedMessage.mediaType === MediaType.INTERACTIVE_LIST
    ) {
      const departmentSelected = message.quotedMessage.rows?.find(
        row => row.id.toString() === message.text
      );
      const msgText = departmentSelected
        ? `${message.text}.&nbsp;&nbsp;${departmentSelected.title}`
        : message.text;

      return <RenderMarkdown markdown={msgText} />;
    }

    const notDeliveredNotifyData =
      SpecialMessageParser.isMsgNotDeliveredNotification(message?.text);

    if (message?.catalogueProduct) return null;

    if (notDeliveredNotifyData?.message)
      return <RenderMarkdown markdown={notDeliveredNotifyData.message} />;
    return <RenderMarkdown markdown={message?.text} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncomingMsg, message]);

  return <>{messageContent}</>;
};

export default ChatMessageContent;
